/**
 * Tolerant parser for the W3C `Server-Timing` response header emitted by the
 * backend. Header shape:
 *
 *   Server-Timing: db;dur=498.1, orm;dur=980.4, app;dur=2642.1, total;dur=3138.9
 *
 * **Important — `orm` is not part of a stacked breakdown.** `orm` is a *sum*
 * of Prisma operation wall-times, not an additive segment of `total`: on a
 * parallelised request it can exceed `total` (measured 1063.4 against 183.7
 * on this codebase). Browser devtools render `Server-Timing` entries as a bar
 * chart, so `orm` will draw longer than the request itself — that's expected,
 * not a bug. Never present `db` + `orm` + `app` as summing to `total`.
 *
 * This parser must never throw. A missing, empty, or malformed header, an
 * unrecognised metric name, or a metric with no (or a non-numeric) `dur`
 * yields `null` or a partial object containing only the metrics that parsed
 * cleanly (AC-31).
 */

export interface ServerTiming {
  db?: number;
  orm?: number;
  app?: number;
  total?: number;
}

const KNOWN_METRICS = ['db', 'orm', 'app', 'total'] as const;
type KnownMetric = (typeof KNOWN_METRICS)[number];

function isKnownMetric(name: string): name is KnownMetric {
  return (KNOWN_METRICS as readonly string[]).includes(name);
}

/**
 * Split on `separator`, ignoring separators inside a double-quoted string.
 *
 * Needed because the W3C grammar allows a `desc="…"` parameter whose quoted
 * value may itself contain `,` or `;` — `db;desc="query, count=12";dur=498.1`
 * is *well-formed*, but a naive `split(',')` tears it in half and silently
 * loses `db`'s duration. This backend never emits `desc` (SPEC only ever sends
 * `dur`), so nothing we control can trigger it; a proxy or CDN prepending its
 * own `Server-Timing` entry in front of ours can. Dropping a real measurement
 * on legal input is a worse failure than the few lines this costs.
 */
function splitOutsideQuotes(input: string, separator: string): string[] {
  const parts: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < input.length; i += 1) {
    const char = input[i];

    if (inQuotes && char === '\\' && i + 1 < input.length) {
      // Escaped character inside a quoted string — consume both, so an
      // escaped quote cannot end the string early.
      current += char + input[i + 1];
      i += 1;
      continue;
    }
    if (char === '"') {
      inQuotes = !inQuotes;
      current += char;
      continue;
    }
    if (char === separator && !inQuotes) {
      parts.push(current);
      current = '';
      continue;
    }
    current += char;
  }

  parts.push(current);
  return parts;
}

/**
 * Parse a `Server-Timing` header value into `{ db, orm, app, total }`.
 * Returns `null` when the header is absent/empty or when none of its entries
 * yield a recognised metric with a numeric `dur` — the same outcome whether
 * the header was never sent, was stripped by CORS (a cross-origin response
 * only exposes headers named in `Access-Control-Expose-Headers`), or was
 * simply malformed. Callers cannot and needn't distinguish those cases.
 */
export function parseServerTiming(header: string | null | undefined): ServerTiming | null {
  if (!header) return null;

  const result: ServerTiming = {};

  for (const rawEntry of splitOutsideQuotes(header, ',')) {
    const entry = rawEntry.trim();
    if (!entry) continue; // tolerate stray commas / "garbage;;"

    const parts = splitOutsideQuotes(entry, ';').map((part) => part.trim());
    const name = parts[0];
    if (!name || !isKnownMetric(name)) continue;

    // Match the parameter named exactly `dur`, not merely one starting with
    // those three letters — `duration=5` is a different (unknown) parameter
    // and must not be read as this metric's duration.
    //
    // Two deliberate asymmetries, neither reachable from our own backend (which
    // emits lowercase names and exactly one `dur` per metric), documented so the
    // behaviour is a choice rather than an accident:
    //   - The parameter NAME is matched case-insensitively (`DUR=` works), but
    //     the metric name above is matched case-sensitively, so `DB;dur=1` is
    //     dropped rather than merged into `db`. Merging would risk two entries
    //     silently colliding.
    //   - A repeated `dur` within one entry takes the FIRST (`find`), while a
    //     repeated metric name across entries takes the LAST (the assignment
    //     below overwrites). Both are arbitrary; neither is worth extra code.
    const durParam = parts
      .slice(1)
      .find((part) => part.slice(0, part.indexOf('=')).trim().toLowerCase() === 'dur');
    if (!durParam) continue; // metric present with no `dur` — skip, don't throw

    const rawValue = durParam.slice(durParam.indexOf('=') + 1);
    if (rawValue.trim() === '') continue;

    const value = Number(rawValue.trim());
    if (!Number.isFinite(value)) continue; // non-numeric `dur` — skip

    result[name] = value;
  }

  return Object.keys(result).length > 0 ? result : null;
}
